/**
 * `<PickListLoader>` — the fail-closed render wrapper for a live pick-list
 * (DNKADM-0008 U1).
 *
 * Pairs with `usePickList`: feed it the hook's result and it renders exactly one
 * of the three fail-closed states — **loading** (skeleton/spinner), **ready**
 * (the caller's `children`, given the loaded items), or **error + Retry** (a
 * `cloud_off` panel + a Retry button that re-fetches LIVE). It NEVER renders a
 * partial or empty-as-success list: on `error` the items are `null`, so children
 * never see a half-loaded list.
 *
 * Generic and contract-free so it serves every external-sourced list/axis: the
 * Membership Plans pickers and the tiers×buckets matrix axes (U3/U4) and later
 * specs (0010/0011). The caller supplies a `label` (woven into the loading/error
 * copy) and a `children` render-prop that receives the COMPLETE `items`.
 *
 * Reference: `admin-ui-technical-spec.md` §1.2; handoff "Pick-list behavior
 * (fail-closed)" — `loading → ready` or `loading → error + Retry`, never a
 * partial list; the `cloud_off` "Couldn't load …" panel + Retry on failure.
 */
import type { ReactNode } from 'react';
import { Alert, Box, Button, CircularProgress, Skeleton, Stack, Typography } from '@mui/material';
import { Icon } from '@/theme';
import type { PickList } from '@/hooks/usePickList';

export interface PickListLoaderProps<T> {
  /** The `usePickList` result to render. */
  list: PickList<T>;
  /**
   * Lower-case noun for the list, woven into the loading/error copy
   * (e.g. `"membership tiers"`, `"the Square catalog"`).
   */
  label: string;
  /** Render the ready state from the COMPLETE items. Called only on `'ready'`. */
  children: (items: T[]) => ReactNode;
  /**
   * Loading presentation. `'skeleton'` (default) fills a block (form/picker
   * slot); `'inline'` is a compact spinner for a dropdown/inline slot.
   */
  loadingVariant?: 'skeleton' | 'inline';
}

/**
 * Render the fail-closed state machine for a `usePickList` result. Exactly one of
 * loading / ready / error is shown; the ready branch alone exposes the items, so
 * a partial/empty-on-failure list can never reach the caller.
 */
export function PickListLoader<T>({
  list,
  label,
  children,
  loadingVariant = 'skeleton',
}: PickListLoaderProps<T>) {
  if (list.status === 'loading') {
    if (loadingVariant === 'inline') {
      return (
        <Stack
          direction="row"
          spacing={1}
          role="status"
          aria-label={`Loading ${label}`}
          sx={{
            alignItems: "center",
            color: 'text.secondary',
            py: 0.5
          }}>
          <CircularProgress size={16} aria-hidden />
          <Typography variant="body2">Loading {label}…</Typography>
        </Stack>
      );
    }
    return (
      <Skeleton
        variant="rounded"
        height={120}
        role="status"
        aria-label={`Loading ${label}`}
        sx={{ borderRadius: 2 }}
      />
    );
  }

  if (list.status === 'error') {
    // Fail-closed: a full error panel + Retry, never a partial/empty list. Retry
    // re-fetches LIVE (no cache).
    return (
      <Alert
        severity="error"
        icon={<Icon name="cloud_off" />}
        action={
          <Button color="inherit" size="small" onClick={list.retry}>
            Retry
          </Button>
        }
        sx={{ alignItems: 'center' }}
      >
        <Box>
          Couldn’t load {label}. The data comes live from the connected system —
          check the connection and try again.
        </Box>
      </Alert>
    );
  }

  // `'ready'`: items is a COMPLETE list (possibly empty, which is a real result).
  return <>{children(list.items ?? [])}</>;
}
